import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trophy, Loader2, RefreshCw, ChevronRight } from "lucide-react";
import { api, toast } from "../../App";

const CHANGE_STYLES = {
  added: "bg-green-600",
  removed: "bg-red-600",
  changed: "bg-orange-500",
  unchanged: "bg-gray-400",
};

const fmtSchedule = (days) => {
  const w = Math.floor((days || 0) / 5);
  const d = (days || 0) % 5;
  return [w ? `${w}w` : "", d ? `${d}d` : ""].filter(Boolean).join(" ") || "0d";
};

const signed = (v, fmt) => `${v >= 0 ? "+" : "−"}${fmt(Math.abs(v))}`;

export const ProjectAwardHistory = ({ project, onProjectUpdated }) => {
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const [sync, setSync] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const currency = project?.currency || "USD";

  const fmtCurrency = (v) => {
    try {
      return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(Number(v || 0));
    } catch {
      return `${currency} ${Number(v || 0).toFixed(0)}`;
    }
  };
  const fmtHours = (v) => `${Math.round((v || 0) * 100) / 100}h`;
  const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  const fetchSync = async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/projects/${project.id}/pending-awards`);
      setSync(res.data);
    } catch {
      setSync(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSync();
  }, [project?.id]);

  const checkTotals = async () => {
    const res = await api.get(`/projects/${project.id}/pending-awards`).catch(() => null);
    setSync(res?.data || null);
    if (res?.data?.has_changes) setConfirmOpen(true);
    else toast.success("Project already matches the awarded total on the estimation");
  };

  const applySync = async () => {
    setApplying(true);
    try {
      const res = await api.post(`/projects/${project.id}/apply-award-sync`);
      toast.success(`Project updated: ${signed(res.data.delta_hours, fmtHours)} · ${signed(res.data.delta_cost, fmtCurrency)}`);
      setConfirmOpen(false);
      await fetchSync();
      onProjectUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update project from awards");
    } finally {
      setApplying(false);
    }
  };

  const history = project?.award_history || [];
  if (!project?.estimation_id && history.length === 0) return null;

  const rows = [
    { label: "Estimated Hours", key: "estimated_hours", fmt: fmtHours },
    { label: "Project Value", key: "base_price", fmt: fmtCurrency },
    { label: "Detailing Cost", key: "detailing_cost", fmt: fmtCurrency },
    { label: "Engineering Cost", key: "engineering_cost", fmt: fmtCurrency },
    { label: "Schedule", key: "schedule_days_total", fmt: fmtSchedule, signedFmt: (v) => `${v >= 0 ? "+" : "−"}${fmtSchedule(Math.abs(v))}` },
  ];

  return (
    <Card className="border border-gray-100 shadow-sm rounded-xl bg-white" data-testid="project-award-history-card">
      <CardHeader className={open ? "" : "pb-6"}>
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 hover:text-md3-primary transition-colors" data-testid="award-history-toggle">
            <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-90" : ""}`} />
            <Trophy className="h-5 w-5 text-yellow-600" />
            Award History
          </button>
          {!open && sync?.award_update_pending && (
            <Badge className="bg-orange-500 text-white" data-testid="award-update-pending-badge">Update pending</Badge>
          )}
          {project?.linked_line_item_ids?.length > 0 && (
            <span className="text-sm text-gray-500 font-normal">
              {project.linked_line_item_ids.length} awarded line item{project.linked_line_item_ids.length === 1 ? "" : "s"}
            </span>
          )}
          {project?.estimation_id && (
            <Button
              size="sm"
              variant="outline"
              className="ml-auto"
              onClick={checkTotals}
              disabled={loading || applying}
              data-testid="check-award-totals-btn"
            >
              <RefreshCw className="h-4 w-4 mr-1" /> Check award totals
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      {open && <CardContent className="space-y-4">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking source estimation…
          </div>
        )}

        {sync?.award_update_pending && (
          <div
            className="flex items-center justify-between gap-3 rounded-md border border-orange-200 bg-orange-50 p-3"
            data-testid="award-update-banner"
          >
            <div className="flex items-center gap-2 text-sm text-orange-900">
              <RefreshCw className="h-4 w-4 shrink-0" />
              <span>
                Award status changed on estimation <b>{sync.estimation_number}</b>. Project should be{" "}
                <b data-testid="award-update-target">
                  {fmtHours(sync.target.estimated_hours)} · {fmtCurrency(sync.target.base_price)} · {fmtSchedule(sync.target.schedule_days_total)}
                </b>{" "}
                (<span data-testid="award-update-delta">
                  {rows
                    .filter((r) => ["estimated_hours", "base_price", "schedule_days_total"].includes(r.key) && Math.abs(sync.delta[r.key]) >= 0.01)
                    .map((r) => `${r.label}: ${(r.signedFmt || ((v) => signed(v, r.fmt)))(sync.delta[r.key])}`)
                    .join(" · ") || "line items changed"}
                </span>).
              </span>
            </div>
            <Button
              size="sm"
              onClick={() => setConfirmOpen(true)}
              disabled={applying}
              data-testid="apply-award-update-btn"
              className="bg-orange-600 hover:bg-orange-700 text-white shrink-0"
            >
              Review & Update
            </Button>
          </div>
        )}

        {history.length === 0 ? (
          <div className="text-sm text-gray-500 italic">No award updates recorded.</div>
        ) : !expanded ? (
          <div className="flex items-center justify-between gap-3 text-sm" data-testid="award-history-summary">
            <span className="text-gray-600">
              {history.length} update{history.length === 1 ? "" : "s"} · latest {fmtDateTime(history[history.length - 1].pushed_at)}
              {history[history.length - 1].total_cost !== undefined && <> · now {fmtHours(history[history.length - 1].total_hours)} · {fmtCurrency(history[history.length - 1].total_cost)}</>}
            </span>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setExpanded(true)} data-testid="award-history-show-btn">Show details</Button>
          </div>
        ) : (
          <>
          <Button size="sm" variant="ghost" className="h-7 text-xs -mt-2" onClick={() => setExpanded(false)} data-testid="award-history-hide-btn">Hide details</Button>
          <ol className="relative border-l border-gray-200 ml-2 space-y-4">
            {history.map((entry, idx) => (
              <li key={`${entry.pushed_at}-${idx}`} className="ml-4" data-testid={`award-history-entry-${idx}`}>
                <div className="absolute -left-1.5 h-3 w-3 rounded-full bg-yellow-500 border border-white" />
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={entry.type === "initial" || !entry.type ? "bg-green-600" : "bg-blue-600"}>
                    {entry.type === "initial" || !entry.type ? "initial" : "update"}
                  </Badge>
                  <span className="text-sm text-gray-600">{fmtDateTime(entry.pushed_at)}</span>
                  <span className="text-sm text-gray-500">by {entry.pushed_by_name || "—"}</span>
                </div>
                <div className="mt-1 text-sm">
                  <b>{signed(entry.hours_added || 0, fmtHours)}</b> · <b>{signed(entry.cost_added || 0, fmtCurrency)}</b>
                  {entry.total_cost !== undefined && (
                    <span className="text-gray-600"> → total {fmtHours(entry.total_hours)} · {fmtCurrency(entry.total_cost)}</span>
                  )}
                  <div className="text-gray-600">
                    {(entry.changes || []).length > 0
                      ? entry.changes.map((c) => `${c.description} (${c.change})`).join(", ")
                      : (entry.snapshot || []).map((s) => s.description).join(", ")}
                  </div>
                </div>
              </li>
            ))}
          </ol>
          </>
        )}
      </CardContent>}

      <AlertDialog open={confirmOpen} onOpenChange={(o) => !o && setConfirmOpen(false)}>
        <AlertDialogContent className="max-w-2xl" data-testid="award-update-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Update Project from Awarded Estimation</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-sm text-gray-700">
                <div>Project values will be set to the total of all line items currently awarded on {sync?.estimation_number} v{sync?.estimation_version}.</div>
                {sync?.delta && sync?.current && sync?.target && (
                  <table className="w-full text-left">
                    <thead>
                      <tr className="text-xs text-gray-500 border-b">
                        <th className="py-1">Field</th><th>Current</th><th>New</th><th>Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const d = sync.delta[r.key];
                        return (
                          <tr key={r.key} className="border-b last:border-0" data-testid={`award-update-row-${r.key}`}>
                            <td className="py-1 font-medium">{r.label}</td>
                            <td>{r.fmt(sync.current[r.key])}</td>
                            <td className="font-semibold">{r.fmt(sync.target[r.key])}</td>
                            <td className={d > 0 ? "text-green-700" : d < 0 ? "text-red-700" : "text-gray-400"}>
                              {Math.abs(d) < 0.01 ? "—" : (r.signedFmt || ((v) => signed(v, r.fmt)))(d)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                <div>
                  <div className="text-xs text-gray-500 mb-1">Line items</div>
                  <ul className="space-y-1">
                    {(sync?.lines || []).map((l) => (
                      <li key={l.id} className="flex items-center gap-2">
                        <Badge className={`${CHANGE_STYLES[l.change]} text-white`}>{l.change}</Badge>
                        <span className={l.awarded ? "" : "line-through text-gray-400"}>{l.description}</span>
                        <span className="ml-auto text-gray-500">{fmtHours(l.estimated_detailing_hours)} · {fmtCurrency(l.subtotal)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="text-xs text-gray-500">Allocated hours and allocated schedule are not changed — update them manually if needed.</div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={applying}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={applySync}
              disabled={applying}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="confirm-award-update"
            >
              {applying ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Confirm & Update
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default ProjectAwardHistory;
